import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity } from "lucide-react";

function timeAgo(iso) {
  const sec = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function ActivityTicker() {
  const [entries, setEntries] = useState([]);
  const [idx, setIdx] = useState(0);
  const [, force] = useState(0); // tick to refresh relative timestamps

  // Fetch real items, then keep refreshing every 20s so new posts show up live.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/items", { params: { status: "available" } })
        .then((r) => {
          if (cancelled) return;
          const live = (r.data || []).slice(0, 8).map((it) => ({
            id: it.id,
            user_name: it.user_name,
            title: it.title,
            where: it.address?.trim() || "the curb",
            created_at: it.created_at,
          }));
          setEntries(live);
          setIdx((i) => (live.length ? i % live.length : 0));
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 20_000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotate to the next entry every ~4s.
  useEffect(() => {
    if (entries.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % entries.length), 4000);
    return () => clearInterval(t);
  }, [entries.length]);

  // Re-render every 30s so "2m ago" stays accurate.
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  if (entries.length === 0) return null;

  const e = entries[idx];

  return (
    <Link to={`/items/${e.id}`} data-testid="activity-ticker"
      className="inline-flex items-center gap-3 pl-3 pr-5 h-11 rounded-full bg-white/60 backdrop-blur border border-white/70 shadow-sm max-w-full hover:bg-white/80 transition-colors">
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#7C3AED] opacity-60 animate-ping" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#7C3AED]" />
      </span>
      <span className="text-xs uppercase tracking-[0.18em] text-[#0F0F12] font-medium hidden sm:inline">
        <Activity className="inline h-3 w-3 mr-1 -mt-0.5" />Live
      </span>
      <span key={e.id} className="text-sm text-[#0F0F12] fade-in-up truncate">
        <strong>{e.user_name}</strong> just left{" "}
        <span className="text-[#7C3AED]">{e.title}</span> on {e.where}
        <span className="text-[#6B7280]"> · {timeAgo(e.created_at)}</span>
      </span>
    </Link>
  );
}
